import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import { ImageSquare } from '@phosphor-icons/react';
import './index.css';

import {
  PlanCardHeaderSyled,
  CarrierLogoBoxStyled,
} from 'enrollment/features/Plans/PlanCard/PlanCardStyled';
import {
  BenefitPlanDto,
  CoverageDto,
  IncrementDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { SpecificIcon } from 'enrollment/utils/common/Utils';
import { VolumeSelect } from './VolumeSelection/VolumeSelect';
import { ElectRequestDto } from '../PlanCard/types';
import { volumeElectionParams } from './utils';
import { SingleIncrementCard } from './components/SingleIncrementCard';
import { EsignatureModal } from '../EsignatureModal';
import { RiderBenefits } from 'enrollment/components/RiderBenefits';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

export interface IncrementType {
  increment: string;
  volume: string;
  subscriber_premium: string;
  requires_eoi: boolean;
  allowed_volume: string;
}

export interface VolumeCardProps {
  plan: BenefitPlanDto;
  orgBenefitId?: number;
  enrollmentContextType?: string;
  selectedDependentIds: number[];
  coverage?: CategoryCoverageDto | CoverageDto;
  color: string;
  benefitLookup?: string;
  updateElection: (params: ElectRequestDto) => void;
  showEsignatureModal?: boolean;
  orgPlanYearId: number;
  increments: IncrementDto[];
  hideEmptyCost: boolean;
  confirmButtonOverrideLabel?: string;
}

export const VolumeCard = ({
  plan,
  orgBenefitId,
  enrollmentContextType,
  selectedDependentIds,
  coverage,
  color,
  benefitLookup,
  updateElection,
  showEsignatureModal,
  orgPlanYearId,
  increments,
  hideEmptyCost,
  confirmButtonOverrideLabel,
}: VolumeCardProps) => {
  const { t } = useTranslation('pages.volume');
  const isMobile = useMediaQuery('(max-width: 744px)');

  const [electedRiders, setElectedRiders] = useState<number[]>([]);
  const incremenetObj = useMemo(() => increments || [], [plan]);
  const requestedVolume = coverage?.req_volume || '';
  const requestedIncrement = incremenetObj.find(
    (increment) =>
      parseFloat(increment.volume).toFixed(2) ===
      parseFloat(requestedVolume).toFixed(2)
  );
  const singleIncrement = incremenetObj.length === 1;

  const defaultIncrement = {
    increment: '0',
    volume: 'Select Amount',
    subscriber_premium: '0.0',
    requires_eoi: false,
  } as IncrementType;

  const [selectedIncrement, setSelectedIncrement] = useState<IncrementType>(
    requestedIncrement || defaultIncrement
  );

  const [sliderValue, setSliderValue] = useState(
    Number(selectedIncrement.volume) || 0
  );

  const handleSliderChange = (newVolume: string) => {
    const newIncrement = incremenetObj.find((inc) => inc.volume === newVolume);

    if (newIncrement) {
      setSelectedIncrement(newIncrement);
      setSliderValue(parseFloat(newVolume));
    }
  };

  const handleElection = (data?: any) => {
    const selectedVolume = selectedIncrement.volume;
    const selectedIncrementValue = selectedIncrement.increment;

    if (data) {
      updateElection(
        volumeElectionParams({
          plan,
          orgBenefitId,
          enrollmentContextType,
          selectedDependentIds,
          selectedIncrementValue,
          selectedVolume,
          esignPassword: data?.esign_password,
          esignChecked: data?.esign_checked,
          electedRiders,
        })
      );
    } else {
      updateElection(
        volumeElectionParams({
          plan,
          orgBenefitId,
          enrollmentContextType,
          selectedDependentIds,
          selectedIncrementValue,
          selectedVolume,
          electedRiders,
        })
      );
    }
  };

  const emptyFunction = () => {
    return;
  };

  const electVolume = () => {
    if (!selectedIncrement?.volume) {
      return;
    }

    handleElection();
  };

  useEffect(() => {
    if (singleIncrement) {
      setSelectedIncrement(incremenetObj[0]);
    }
  }, [singleIncrement, increments]);

  return (
    <div className="card p-0" data-testid="volume-card-container">
      <PlanCardHeaderSyled>
        <div className={`icon-container ${color}`}>
          <SpecificIcon benefitType={benefitLookup ? benefitLookup : ''} />
        </div>
        <div className="d-none d-md-inline h3 ml-3_5 plan-card">
          {plan.plan_name}
        </div>

        <CarrierLogoBoxStyled>
          {plan?.carrier_logo_url ? (
            <img
              src={plan?.carrier_logo_url}
              alt=""
              data-testid="carrier-logo"
            />
          ) : (
            <div className="d-flex align-items-center">
              <ImageSquare size={32} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{plan.carrier}</Typography>
            </div>
          )}
        </CarrierLogoBoxStyled>
      </PlanCardHeaderSyled>
      {singleIncrement ? (
        <SingleIncrementCard
          increment={incremenetObj[0]}
          benefitType={benefitLookup ? benefitLookup : ''}
          hideEmptyCost={hideEmptyCost}
        />
      ) : (
        <VolumeSelect
          increments={increments}
          onSliderChange={handleSliderChange}
          selectedIncrement={selectedIncrement}
          sliderValue={sliderValue}
          setSliderValue={setSliderValue}
          setSelectedIncrement={setSelectedIncrement}
          planName={plan.plan_name}
          planId={plan?.plan_id}
          hideEmptyCost={hideEmptyCost}
        />
      )}
      <RiderBenefits
        riderInfo={plan.org_plan_rider_benefits as PlanRiderDto[]}
        riderCoverages={coverage?.rider_coverages || []}
        planPremium={parseFloat(selectedIncrement.subscriber_premium)}
        electedRiders={electedRiders}
        setElectedRiders={setElectedRiders}
        orgPlanYearId={orgPlanYearId}
        displayCheckbox
        smallMobileMargins={true}
        hideEmptyCost={hideEmptyCost}
      />
      <div
        className={`border-top p-4 d-flex ${isMobile ? 'justify-content-center' : ''}`}
      >
        {showEsignatureModal ? (
          <EsignatureModal
            setEsignElectionData={handleElection}
            carrierLogoUrl={plan?.carrier_logo_url}
            carrierName={plan?.carrier}
            data={plan?.esign_data}
            planId={plan?.plan_id}
            setModalRequired={emptyFunction}
          />
        ) : (
          <ps-button
            id="elect-volume"
            other-data={{ 'data-testid': 'elect-volume-btn' }}
            level="primary"
            label={
              confirmButtonOverrideLabel
                ? confirmButtonOverrideLabel
                : t('looksGood')
            }
            {...{ onClick: () => electVolume() }}
          ></ps-button>
        )}
      </div>
    </div>
  );
};
